import { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import { addMediaQueryListener, removeMediaQueryListener } from '../lib/mediaQuery';
import '../styles/DynamicNav.css';

export interface NavItem {
  label: string;
  path: string;
  icon?: string;
  description?: string;
  children?: NavItem[];
}

export interface SearchIndexItem {
  label: string;
  path: string;
  keywords?: string[];
  icon?: string;
}

interface DynamicNavProps {
  items: NavItem[];
  currentPath?: string;
  onNavigate?: (path: string) => void;
  /** Optional slot rendered on the right side of the nav bar (e.g. auth button). */
  authSlot?: ReactNode;
  controlsSlot?: ReactNode;
  searchIndex: SearchIndexItem[]; // kept for future use / external consumers
  ctas: Array<{ label: string; path: string }>;
  dimensionStrip: Array<{ label: string; path: string; icon: string }>;
  brand: string;
  searchPlaceholder: string;
  /** Called when the user activates the search input — opens the full SearchModal. */
  onSearchOpen?: () => void;
}

export const DynamicNav = ({
  items,
  currentPath = '/',
  onNavigate,
  authSlot,
  controlsSlot,
  searchIndex: _searchIndex, // retained for interface compatibility
  ctas,
  dimensionStrip,
  brand,
  searchPlaceholder,
  onSearchOpen,
}: DynamicNavProps) => {
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [compact, setCompact] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navRef = useRef<HTMLElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const placeOpenDropdown = useCallback(() => {
    const menu = dropdownRef.current;
    if (!menu || !openGroup) return;

    // Hamburger panel uses in-flow (static) dropdowns; skip viewport clamping.
    if (getComputedStyle(menu).position === 'static') {
      menu.style.left = '';
      menu.style.right = '';
      menu.style.maxWidth = '';
      menu.style.maxHeight = '';
      return;
    }

    const margin = 8;
    const vw = window.visualViewport?.width ?? window.innerWidth;
    const vh = window.visualViewport?.height ?? window.innerHeight;
    const offsetLeft = window.visualViewport?.offsetLeft ?? 0;
    const offsetTop = window.visualViewport?.offsetTop ?? 0;

    menu.style.left = '0px';
    menu.style.right = 'auto';
    menu.style.maxWidth = `${Math.max(0, vw - margin * 2)}px`;

    const rect = menu.getBoundingClientRect();
    let shift = 0;
    const rightLimit = offsetLeft + vw - margin;
    const leftLimit = offsetLeft + margin;
    if (rect.right > rightLimit) shift -= rect.right - rightLimit;
    if (rect.left + shift < leftLimit) shift += leftLimit - (rect.left + shift);
    menu.style.left = `${shift}px`;

    const after = menu.getBoundingClientRect();
    const available = offsetTop + vh - after.top - margin;
    menu.style.maxHeight = `${Math.max(140, available)}px`;
  }, [openGroup]);

  // Close dropdowns + hamburger when clicking outside the nav
  useLayoutEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    const update = () => {
      document.documentElement.style.setProperty('--nav-height', `${nav.offsetHeight}px`);
    };
    update();
    if (typeof ResizeObserver === 'function') {
      const ro = new ResizeObserver(update);
      ro.observe(nav);
      return () => {
        ro.disconnect();
        document.documentElement.style.removeProperty('--nav-height');
      };
    }
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('resize', update);
      document.documentElement.style.removeProperty('--nav-height');
    };
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setOpenGroup(null);
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Close hamburger on resize to desktop
  useEffect(() => {
    const media = window.matchMedia('(max-width: 768px)');
    const onResize = () => {
      if (!media.matches) setMenuOpen(false);
    };
    addMediaQueryListener(media, onResize);
    return () => removeMediaQueryListener(media, onResize);
  }, []);

  useEffect(() => {
    const media = window.matchMedia('(max-width: 980px)');
    const update = () => setCompact(media.matches);
    update();
    addMediaQueryListener(media, update);
    return () => removeMediaQueryListener(media, update);
  }, []);

  useLayoutEffect(() => {
    placeOpenDropdown();
    window.addEventListener('resize', placeOpenDropdown);
    window.addEventListener('scroll', placeOpenDropdown, true);
    window.visualViewport?.addEventListener('resize', placeOpenDropdown);
    window.visualViewport?.addEventListener('scroll', placeOpenDropdown);
    return () => {
      window.removeEventListener('resize', placeOpenDropdown);
      window.removeEventListener('scroll', placeOpenDropdown, true);
      window.visualViewport?.removeEventListener('resize', placeOpenDropdown);
      window.visualViewport?.removeEventListener('scroll', placeOpenDropdown);
    };
  }, [placeOpenDropdown]);

  const navigate = useCallback((path: string) => {
    if (onNavigate) onNavigate(path);
    else window.location.href = path;
    setOpenGroup(null);
    setMenuOpen(false);
  }, [onNavigate]);

  const isGroupActive = (item: NavItem) =>
    currentPath === item.path ||
    (item.children?.some(c => c.path === currentPath) ?? false);

  return (
    <nav className={`dynamic-nav ${compact ? 'dynamic-nav--compact' : ''}`} ref={navRef}>
      <div className="nav-shell">
        {/* ── Top bar: always visible ───────────────────────────────── */}
        <div className="nav-top-row">
          <button className="nav-brand" onClick={() => navigate('/')}>
            <span className="nav-brand-mark">ॐ</span>
            <span className="nav-brand-text">{brand}</span>
          </button>

          {/* Search — hidden on mobile, visible on desktop */}
          <div className="nav-search-wrap">
            <input
              className="nav-search"
              value={searchQuery}
              onFocus={() => { onSearchOpen?.(); setSearchQuery(''); }}
              onChange={(e) => { setSearchQuery(e.target.value); onSearchOpen?.(); }}
              onClick={() => onSearchOpen?.()}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
              readOnly={!!onSearchOpen}
            />
          </div>

          {/* Toolbar (controls + CTAs) — hidden on mobile, visible on desktop */}
          <div className="nav-toolbar nav-toolbar--top">
            {controlsSlot}
            {ctas.map((cta, index) => (
              <button key={cta.path} type="button" className={`nav-cta ${index === 0 ? 'nav-cta--primary' : 'nav-cta--secondary'}`} onClick={() => navigate(cta.path)}>
                {cta.label}
              </button>
            ))}
          </div>

          {/* Auth — always visible in top row */}
          {authSlot && <div className="nav-auth-slot">{authSlot}</div>}

          {/* Hamburger — mobile only */}
          <button
            className="nav-hamburger"
            onClick={() => { setMenuOpen(v => !v); setOpenGroup(null); }}
            aria-expanded={menuOpen}
            aria-controls="nav-collapse-panel"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          >
            <span aria-hidden="true">{menuOpen ? '✕' : '☰'}</span>
          </button>
        </div>

        {/* ── Collapsible panel ─────────────────────────────────────── */}
        {/* desktop: display:contents (transparent passthrough)         */}
        {/* mobile:  hidden unless menuOpen                             */}
        <div
          id="nav-collapse-panel"
          className={`nav-collapse-panel${menuOpen ? ' nav-collapse-panel--open' : ''}`}
        >
          {/* Search inside panel — mobile only (hidden on desktop via CSS) */}
          <div className="nav-search-wrap nav-search-wrap--panel">
            <input
              className="nav-search"
              value={searchQuery}
              onFocus={() => { onSearchOpen?.(); setSearchQuery(''); }}
              onChange={(e) => { setSearchQuery(e.target.value); onSearchOpen?.(); }}
              onClick={() => onSearchOpen?.()}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
              readOnly={!!onSearchOpen}
            />
          </div>

          {/* Toolbar inside panel — mobile only (hidden on desktop via CSS) */}
          <div className="nav-toolbar nav-toolbar--panel">
            {controlsSlot}
            {ctas.map((cta, index) => (
              <button key={cta.path} type="button" className={`nav-cta ${index === 0 ? 'nav-cta--primary' : 'nav-cta--secondary'}`} onClick={() => navigate(cta.path)}>
                {cta.label}
              </button>
            ))}
          </div>

          {dimensionStrip.length > 0 && (
          <div className="nav-strip" aria-label="12-dimension strip">
            {dimensionStrip.map((item) => (
              <button key={item.path} type="button" className={`nav-strip-chip ${currentPath === item.path ? 'active' : ''}`} onClick={() => navigate(item.path)}>
                <span className="nav-strip-icon">{item.icon}</span>
                <span>{item.label}</span>
              </button>
            ))}
          </div>
          )}

          <div className="nav-menu-row">
            {items.map((item) => {
              if (item.children && item.children.length > 0) {
                const isOpen = openGroup === item.path;
                const isActive = isGroupActive(item);
                return (
                  <div key={item.path} className={`nav-group ${isActive ? 'active' : ''} ${isOpen ? 'nav-group--open' : ''}`}>
                    <button
                      className={`nav-item nav-group-btn ${isActive ? 'active' : ''}`}
                      onClick={() => setOpenGroup(isOpen ? null : item.path)}
                      aria-expanded={isOpen}
                      aria-haspopup="true"
                      title={item.label}
                    >
                      {item.icon && <span className="nav-icon">{item.icon}</span>}
                      <span className="nav-label">{item.label}</span>
                      <span className="nav-chevron" aria-hidden="true">{isOpen ? '▲' : '▼'}</span>
                    </button>
                    {isOpen && (
                      <div ref={dropdownRef} className="nav-dropdown nav-mega-dropdown" role="menu">
                        <div className="nav-mega-summary">
                          <strong>{item.label}</strong>
                          {item.description && <p>{item.description}</p>}
                        </div>
                        <div className="nav-mega-grid">
                          {item.children.map((child) => (
                            <button
                              key={child.path}
                              className={`nav-dropdown-item nav-mega-item ${currentPath === child.path ? 'active' : ''}`}
                              onClick={() => navigate(child.path)}
                              role="menuitem"
                            >
                              {child.icon && <span className="nav-icon">{child.icon}</span>}
                              <span className="nav-mega-copy">
                                <span className="nav-mega-title">{child.label}</span>
                                {child.description && <span className="nav-mega-desc">{child.description}</span>}
                              </span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <button
                  key={item.path}
                  className={`nav-item ${currentPath === item.path ? 'active' : ''}`}
                  onClick={() => navigate(item.path)}
                  title={item.label}
                >
                  {item.icon && <span className="nav-icon">{item.icon}</span>}
                  <span className="nav-label">{item.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default DynamicNav;
